import { databases } from "@/lib/appwrite";
import { isCertificateApproved } from "@/requests/certificates/employmentCertificate/views/config/certificateApprovals";

interface UpdateCertificateBySupervisorParams {
    requestId: string;
    action: 'approve' | 'reject';
    supervisorUserId: string;
    rejectionReason?: string;
    certificateType: string;
    currentStatusByRrhh: string;
    currentStatusByManager: string;
}

export const updateCertificateRequestBySupervisor = async ({
    requestId,
    action,
    supervisorUserId,
    rejectionReason,
    certificateType,
    currentStatusByRrhh,
    currentStatusByManager
}: UpdateCertificateBySupervisorParams) => {
    try {
        const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
        const collectionId = import.meta.env.VITE_CERTIFICATE_REQUESTS_COLLECTION_ID;

        if (!databaseId || !collectionId) {
            throw new Error("Missing environment variables for database or collection");
        }

        // Preparar los datos base de actualización
        const updateData: any = {};

        if (action === 'approve') {
            // APROBACIÓN POR SUPERVISOR
            updateData.statusBySupervisor = 'approved';
            updateData.idApprovedBySupervisor = supervisorUserId;
            
            // Verificar si este certificado ya está completamente aprobado
            // Simular el nuevo estado con Supervisor aprobado
            const isFullyApproved = isCertificateApproved(
                certificateType,
                currentStatusByRrhh,
                'approved', // Supervisor está aprobando ahora
                currentStatusByManager
            );

            if (isFullyApproved) {
                // Si ya tiene todas las aprobaciones requeridas, actualizar status y approvedAt
                updateData.status = 'approved';
                updateData.approvedAt = new Date().toISOString();
            } else {
                // Si aún faltan aprobaciones (por ejemplo, Manager), mantener status como pending
                updateData.status = 'pending';
            }

        } else if (action === 'reject') {
            // RECHAZO POR SUPERVISOR
            if (!rejectionReason || rejectionReason.trim() === '') {
                throw new Error("El motivo del rechazo es obligatorio");
            }

            updateData.statusBySupervisor = 'rejected';
            updateData.commentsSupervisor = rejectionReason;
            updateData.idApprovedBySupervisor = supervisorUserId; // Registrar quién rechazó
            
            // Si Supervisor rechaza, el status general es rechazado
            updateData.status = 'rejected';
        }

        // Ejecutar la actualización
        const response = await databases.updateDocument(
            databaseId,
            collectionId,
            requestId,
            updateData
        );

        return {
            success: true,
            data: response,
            message: action === 'approve' 
                ? 'Solicitud aprobada exitosamente' 
                : 'Solicitud rechazada exitosamente'
        };

    } catch (error) {
        console.error("Error updating certificate request by Supervisor:", error);
        return {
            success: false,
            error: error instanceof Error ? error.message : "Error desconocido al actualizar la solicitud"
        };
    }
};

// Función auxiliar para obtener el estado actualizado de una solicitud
export const getUpdatedCertificateRequest = async (requestId: string) => {
    try {
        const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
        const collectionId = import.meta.env.VITE_CERTIFICATE_REQUESTS_COLLECTION_ID;

        const response = await databases.getDocument(
            databaseId,
            collectionId,
            requestId
        );

        return {
            success: true,
            data: response
        };
    } catch (error) {
        console.error("Error getting updated certificate request:", error);
        return {
            success: false,
            error: error instanceof Error ? error.message : "Error al obtener la solicitud"
        };
    }
};
